import React, { useState, useEffect, useContext } from 'react';
import { timeAgo } from '../utils/timeAgo';
import { doc, getDoc, collection, query, orderBy, getDocs, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import ProfilePicture from './ProfilePicture';
import { AuthContext } from '../contexts/AuthContext';
import { FaTrash } from 'react-icons/fa';

const Comment = ({ comment, onReply, currentUser, postId }) => {
    const [showReplyInput, setShowReplyInput] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [userData, setUserData] = useState(null);
    const [replies, setReplies] = useState([]);
    const [isLoadingReplies, setIsLoadingReplies] = useState(false);
    const { currentUser: user } = useContext(AuthContext);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const userRef = doc(db, 'profiles', comment.userId);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    setUserData(userSnap.data());
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        };

        const fetchReplies = async () => {
            try {
                setIsLoadingReplies(true);
                const repliesRef = collection(db, 'posts', postId, 'comments', comment.id, 'replies');
                const repliesQuery = query(repliesRef, orderBy('createdAt', 'asc'));
                const repliesSnap = await getDocs(repliesQuery);
                const repliesData = repliesSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setReplies(repliesData);
            } catch (error) {
                console.error('Error fetching replies:', error);
            } finally {
                setIsLoadingReplies(false);
            }
        };

        fetchUserData();
        fetchReplies();
    }, [comment.userId, comment.id, postId]);

    const handleReplySubmit = async (e) => {
        e.preventDefault();
        if (replyText.trim()) {
            await onReply(comment.id, replyText.trim());
            setReplyText('');
            setShowReplyInput(false);
        }
    };

    const handleDeleteComment = async () => {
        if (window.confirm('Are you sure you want to delete this comment?')) {
            try {
                await deleteDoc(doc(db, 'posts', postId, 'comments', comment.id));
                // You might want to add a callback to refresh the comments list in the parent component
                window.location.reload();
            } catch (error) {
                console.error('Error deleting comment:', error);
                alert('Failed to delete comment. Please try again.');
            }
        }
    };

    const isCommentAuthor = user && user.uid === comment.userId;

    return (
        <div className="flex space-x-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors duration-200">
            <div className="flex-shrink-0">
                <ProfilePicture
                    imageUrl={userData?.profilePic || userData?.photoURL}
                    onUpdate={null}
                    size="sm"
                />
            </div>
            <div className="flex-grow">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer">
                            {userData?.username || 'Anonymous'}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                            {timeAgo(comment.createdAt)}
                        </span>
                    </div>
                    {isCommentAuthor && (
                        <button
                            onClick={handleDeleteComment}
                            className="text-red-500 hover:text-red-700 transition-colors duration-200"
                        >
                            <FaTrash className="h-4 w-4" />
                        </button>
                    )}
                </div>
                <p className="mt-1 text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{comment.content}</p>
                
                {user && (
                    <div className="mt-2">
                        <button
                            onClick={() => setShowReplyInput(!showReplyInput)}
                            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200"
                        >
                            Reply
                        </button>
                    </div>
                )}

                {showReplyInput && (
                    <form onSubmit={handleReplySubmit} className="mt-3">
                        <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Write a reply..."
                            className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-none"
                            rows="2"
                            maxLength={500}
                        />
                        <div className="mt-2 flex justify-between items-center">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {500 - replyText.length} characters remaining
                            </span>
                            <div className="flex space-x-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowReplyInput(false);
                                        setReplyText('');
                                    }}
                                    className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!replyText.trim()}
                                    className="px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200"
                                >
                                    Reply
                                </button>
                            </div>
                        </div>
                    </form>
                )}

                {isLoadingReplies ? (
                    <div className="mt-3 pl-6 flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">Loading replies...</span>
                    </div>
                ) : replies.length > 0 && (
                    <div className="mt-3 space-y-3 pl-6 border-l-2 border-gray-200 dark:border-gray-700">
                        {replies.map((reply) => (
                            <div key={reply.id} className="flex space-x-3 hover:bg-gray-100 dark:hover:bg-gray-750 rounded-lg p-2 transition-colors duration-200">
                                <div className="flex-shrink-0">
                                    <ProfilePicture
                                        imageUrl={reply.userPhotoURL}
                                        onUpdate={null}
                                        size="sm"
                                    />
                                </div>
                                <div className="flex-grow">
                                    <div className="flex items-center space-x-2">
                                        <span className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer">
                                            {reply.userDisplayName || 'Anonymous'}
                                        </span>
                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                            {timeAgo(reply.createdAt)}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{reply.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Comment;
